import { lazy, Suspense } from "react";
import { Navigate, useLocation } from "react-router-dom";

import RouteStampFooter from "./components/RouteStampFooter";
import DashboardLayout from "./components/DashboardLayout";
import useSupabaseSession, { type SupabaseSessionLike } from "./hooks/useSupabaseSession";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import LoginPage from "./pages/LoginPage";
import { AlertsProvider } from "./contexts/AlertsContext";

// Injected at build time by vite.config.ts `define`. Declared here for TypeScript.
declare const __DEMO_MODE__: boolean;

const OverviewPage      = lazy(() => import("./pages/OverviewPage"));
const FleetPage         = lazy(() => import("./pages/FleetPage"));
const ContinuityPage    = lazy(() => import("./pages/ContinuityPage"));
const EvidencePacksPage = lazy(() => import("./pages/EvidencePacksPage"));
const AlertsPage        = lazy(() => import("./pages/AlertsPage"));
const AccessAuditPage   = lazy(() => import("./pages/AccessAuditPage"));
const SettingsPage      = lazy(() => import("./pages/SettingsPage"));

export type RouteDebugInfo = {
  path: string;
  route: "login" | "auth" | "dashboard" | "overview" | "fleet" | "continuity" | "evidence" | "alerts" | "access" | "settings";
};

export function matchRoute(pathname: string): RouteDebugInfo {
  if (pathname === "/login") {
    return { path: pathname, route: "login" };
  }

  if (pathname === "/auth") {
    return { path: pathname, route: "auth" };
  }

  if (pathname === "/" ) {
    return { path: pathname, route: "overview" };
  }

  if (pathname === "/fleet" || pathname.startsWith("/fleet/")) {
    return { path: pathname, route: "fleet" };
  }

  if (pathname === "/continuity" || pathname.startsWith("/continuity/")) {
    return { path: pathname, route: "continuity" };
  }

  if (pathname === "/evidence" || pathname.startsWith("/evidence/")) {
    return { path: pathname, route: "evidence" };
  }

  if (pathname === "/alerts" || pathname.startsWith("/alerts/")) {
    return { path: pathname, route: "alerts" };
  }

  if (pathname === "/access" || pathname.startsWith("/access/")) {
    return { path: pathname, route: "access" };
  }

  if (pathname === "/settings" || pathname.startsWith("/settings/")) {
    return { path: pathname, route: "settings" };
  }

  return { path: pathname, route: "dashboard" };
}

export function resolveRoutePath(pathname: string): JSX.Element {
  const auth = { loading: false, session: null as SupabaseSessionLike };
  return resolveRoutePathWithAuth(pathname, auth);
}

function RouteGuardLoading(): JSX.Element {
  return <p style={{ padding: "var(--gutter)" }}>Loading authentication…</p>;
}

function RequireAuth(props: { loading: boolean; session: SupabaseSessionLike; children: JSX.Element }): JSX.Element {
  // In demo mode the dashboard is accessible without a real session.
  if (__DEMO_MODE__) {
    return props.children;
  }

  if (props.loading) {
    return <RouteGuardLoading />;
  }

  if (!props.session) {
    return <Navigate to="/login" replace />;
  }

  return props.children;
}

function RedirectIfAuthed(props: { loading: boolean; session: SupabaseSessionLike; children: JSX.Element }): JSX.Element {
  if (props.loading) {
    return <RouteGuardLoading />;
  }

  if (props.session) {
    return <Navigate to="/fleet" replace />;
  }

  return props.children;
}

const PAGE_FALLBACK = <p style={{ padding: "var(--gutter)" }}>Loading…</p>;

function DashboardPage(props: { children: JSX.Element }): JSX.Element {
  return (
    <AlertsProvider>
      <DashboardLayout>
        <Suspense fallback={PAGE_FALLBACK}>{props.children}</Suspense>
      </DashboardLayout>
    </AlertsProvider>
  );
}

export function resolveRoutePathWithAuth(
  pathname: string,
  auth: { loading: boolean; session: SupabaseSessionLike }
): JSX.Element {
  const route = matchRoute(pathname);

  if (route.route === "login") {
    if (__DEMO_MODE__) return <Navigate to="/" replace />;
    return (
      <RedirectIfAuthed loading={auth.loading} session={auth.session}>
        <LoginPage />
      </RedirectIfAuthed>
    );
  }

  if (route.route === "auth") {
    return <AuthCallbackPage />;
  }

  const pageMap: Partial<Record<RouteDebugInfo["route"], JSX.Element>> = {
    overview:   <OverviewPage />,
    fleet:      <FleetPage />,
    continuity: <ContinuityPage />,
    evidence:   <EvidencePacksPage />,
    alerts:     <AlertsPage />,
    access:     <AccessAuditPage />,
    settings:   <SettingsPage />,
  };

  const page = pageMap[route.route] ?? null;
  if (page) {
    return (
      <RequireAuth loading={auth.loading} session={auth.session}>
        <DashboardPage>{page}</DashboardPage>
      </RequireAuth>
    );
  }

  // Unknown path → redirect to overview
  return <Navigate to="/" replace />;
}

export default function RouteApp(props: { path?: string }): JSX.Element {
  const auth = useSupabaseSession();
  const location = useLocation();
  const pathname = props.path ?? location.pathname;
  const route = matchRoute(pathname);
  return (
    <>
      {resolveRoutePathWithAuth(pathname, auth)}
      <RouteStampFooter routeInfo={route} />
    </>
  );
}
